import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import CoachCodePreview from "@/components/CoachCodePreview";
import { useCoachCodeLookup, normalizeCoachCode } from "@/hooks/useCoachCodeLookup";
import {
  MessageSquare, ExternalLink, Mail, Phone, Users,
  UserPlus, UserCheck, MapPin,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function coachPictureUrl(picture) {
  if (!picture) return undefined;
  if (picture.startsWith("http")) return picture;
  return `${API_BASE}${picture.startsWith("/") ? picture : `/${picture}`}`;
}

export default function MyCoach() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachCode, setCoachCode] = useState("");
  const [linking, setLinking] = useState(false);
  const { preview: coachPreview, checking: coachChecking } = useCoachCodeLookup(coachCode);

  useEffect(() => {
    if (!user?.coach_id) {
      setLoading(false);
      return;
    }
    api.get("/students/my-coach")
      .then((r) => setCoach(r.data))
      .catch(() => setCoach(null))
      .finally(() => setLoading(false));
  }, [user?.coach_id]);

  const handleLinkCoach = async (e) => {
    e.preventDefault();
    if (!coachCode.trim()) return;
    setLinking(true);
    try {
      const { data } = await api.post("/students/link-coach", { coach_code: coachCode.trim().toUpperCase() });
      toast.success("Coach associé avec succès !");
      setCoachCode("");
      if (data?.coach) setCoach(data.coach);
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code coach invalide");
    } finally {
      setLinking(false);
    }
  };

  if (!user?.coach_id) {
    return (
      <div data-testid="my-coach-page">
        <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Mon Coach</h1>
        <Card className="border-orange-200 bg-orange-50/50 mt-6 max-w-lg">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold text-slate-900">Pas encore de coach ?</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Entrez le code de votre coach pour accéder à ses plans et services.
                </p>
                <form onSubmit={handleLinkCoach} className="mt-4 space-y-2">
                  <div className="flex gap-3">
                    <Input
                      value={coachCode}
                      onChange={(e) => setCoachCode(normalizeCoachCode(e.target.value))}
                      placeholder="COACH-XXXXXX"
                      className="h-11 bg-white uppercase font-mono tracking-wider"
                    />
                    <Button
                      type="submit"
                      disabled={linking || !coachCode.trim()}
                      className="bg-orange-500 hover:bg-orange-600 text-white rounded-full h-11 px-6 shrink-0"
                    >
                      {linking ? "..." : "Rejoindre"}
                    </Button>
                  </div>
                  <CoachCodePreview preview={coachPreview} checking={coachChecking} codeLength={coachCode.trim().length} />
                </form>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div data-testid="my-coach-page">
        <Skeleton className="h-9 w-48 mb-6" />
        <Card className="border-slate-100">
          <CardContent className="p-8">
            <div className="flex items-center gap-5">
              <Skeleton className="w-20 h-20 rounded-2xl" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-56" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="my-coach-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Mon Coach</h1>
      <p className="text-slate-500 mt-1 text-sm">Votre accompagnateur personnel</p>

      <Card className="border-blue-100 mt-6 overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-blue-600 to-indigo-600" />
        <CardContent className="px-6 pb-6 -mt-12">
          <div className="flex flex-col sm:flex-row sm:items-end gap-5">
            <Avatar className="w-24 h-24 rounded-2xl border-4 border-white shadow-lg flex-shrink-0">
              <AvatarImage src={coachPictureUrl(coach?.picture)} alt={coach?.name} />
              <AvatarFallback className="rounded-2xl bg-blue-100 text-blue-600 text-2xl font-bold">
                {coach?.name?.[0]?.toUpperCase() || <UserCheck className="w-8 h-8" />}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0 pt-2 sm:pt-0 sm:pb-1">
              <h2 className="font-heading text-2xl font-bold text-slate-900">{coach?.name || "Coach"}</h2>
              {coach?.pseudo && (
                <p className="text-sm text-blue-600 font-medium">@{coach.pseudo}</p>
              )}
              {coach?.punch_line && (
                <p className="text-slate-500 mt-1 italic">"{coach.punch_line}"</p>
              )}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                onClick={() => navigate("/student/messages")}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full gap-2"
                data-testid="message-coach-btn"
              >
                <MessageSquare className="w-4 h-4" /> Message
              </Button>
              {coach?.public_url && (
                <a href={coach.public_url} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-full gap-2">
                    <ExternalLink className="w-4 h-4" /> Profil public
                  </Button>
                </a>
              )}
            </div>
          </div>

          {coach?.bio && (
            <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-100">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{coach.bio}</p>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            {coach?.email && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <a href={`mailto:${coach.email}`} className="text-sm text-slate-700 hover:text-blue-600 truncate">
                  {coach.email}
                </a>
              </div>
            )}
            {coach?.phone && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <a href={`tel:${coach.phone}`} className="text-sm text-slate-700 hover:text-blue-600">
                  {coach.phone}
                </a>
              </div>
            )}
            {coach?.student_count != null && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-700">{coach.student_count} élève{coach.student_count > 1 ? "s" : ""} accompagné{coach.student_count > 1 ? "s" : ""}</span>
              </div>
            )}
            {coach?.public_url && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-500 truncate">Profil public disponible</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}